import {Domain, HandleReturnType, StateToMessagesReturnType} from '../../domain.js';
import {agmDomainUri, agmFailure, agmUnhandledMessage, reasonPeerRemoved} from './constants.js';
import {
    broadcast,
    error,
    isLocalSource,
    isRemoteSource,
    peerAdded,
    peerRemoved,
    success,
    unicast
} from '../../common/messages.js';
import {join as gJoin} from '../global/messages.js';
import {inDomain, joinDomain, leaveDomain, threadStateAndActions} from '../../state/core.js';
import {AgmIncomingType, CommandType, DomainType, MessageType, NodeMessage, SendMessageAction} from '../../node.js';
import {
    byId,
    byIdInDomain,
    byIdInDomainThrows,
    byIdThrows,
    bySourceInDomain,
    isClusterPeer,
    isLocalPeer,
    peersInDomain,
    visiblePeers
} from '../../state/peers.js';
import {peerToAddress} from '../../address.js';
import {nodeId} from '../../common/ids.js';
import * as rst from '../../restrictions.js';
import {forwardRegister, forwardUnregister, registerWithConsumer, removeMethodsFromConsumer, remoteMethodsFilter} from './mthds.js';
import {DomainKeys, Peer, State} from '../../state/types/state.js';
import {DomainInfo} from '../../state/types/domain-registry.js';
import {AgmPeer} from '../../state/types/agm.js';
import {getLogger} from '../../logger.js';
import {
    cancelInterest,
    cancelSubscriptions,
    dropInterest,
    forwardAccepted,
    forwardPost,
    forwardPublish,
    forwardSubscribe,
    forwardUnsubscribe
} from './subscriptions.js';
import {cancelInvocations, completeInvocation, forwardCall, forwardYield} from './calls.js';
import {Identity, OriginType, PeerId, Restrictions, Source} from '../../state/types/common.js';
import {exceptionToReason, Reason, reason, requestToReason} from '../../reason.js';
import {MethodDefWithParsedRestrictions} from '../../state/types/mthds.js';
import {DomainJoin, DomainLeave} from '../global/types/messages.js';
import {
    Accepted,
    Call,
    DropSubscription,
    Post,
    Publish,
    Register,
    Subscribe,
    Unregister,
    Unsubscribe,
    Yield
} from './types/messages.js';
import {register} from './messages.js';
import {IOGateway} from '../../../gateway';

const log = getLogger('gateway.domains.agm');

/*Adds a new peer to the list of peers of an existing peer. Only the local peers receive announcement messages*/
function addPeer(state: State, receiver: Source, newPeer: Peer, existingPeer: Peer): HandleReturnType {
    const {identity: existingIdentity, id: existingId} = existingPeer;
    const existingMethods: MethodDefWithParsedRestrictions[] = Object.values(existingPeer[DomainKeys.Agm]?.methods?.[existingId] || {});
    const newPeerId = newPeer.id;
    const local: boolean = isLocalPeer(newPeer);
    const existingLocal: boolean = isLocalPeer(existingPeer);

    // generate messages announcing the link to both sides
    const actions = new Array<SendMessageAction<MessageType | CommandType>>();

    if (local) {
        actions.push(peerAdded(agmDomainUri, receiver, newPeerId, existingId, existingIdentity, {local: existingLocal}));
    }
    if (existingLocal) {
        actions.push(peerAdded(agmDomainUri, existingPeer.source, existingId, newPeerId, newPeer.identity, {local: local}));
    }

    // and finally register all the existing methods to the consumer
    let action: readonly SendMessageAction<MessageType | CommandType>[];
    [state, action] = registerWithConsumer(state, existingPeer, existingMethods, newPeer);

    return [state, actions.concat(action)];
}

function announcePeer(state: State, receiver: Source, peerId: PeerId): HandleReturnType {
    const peer: AgmPeer = byIdInDomainThrows(state, peerId, DomainKeys.Agm);
    const result: HandleReturnType = visiblePeers(state, DomainKeys.Agm, peer)
        .reduce(([s, actions]: HandleReturnType, el: Peer) => {
            const [newState, moreActions] = addPeer(s, receiver, byId(s, peerId)!, el);
            const result: HandleReturnType = [newState, actions.concat(moreActions)];
            return result;
        }, [state, []] as HandleReturnType);
    return result;
}

function join(state: State, source: Source, peerId: PeerId, identity, parsedRestrictions?: Restrictions): HandleReturnType {

    // todo log-action
    state = joinDomain(state, peerId, DomainKeys.Agm, parsedRestrictions);
    return announcePeer(state, source, peerId);
}

/**
 * Remote peer is joining the domain.
 *
 * Updates the state and generates messages to all existing peers announcing the new peer
 */
function remoteJoin(state: State, source: Source, request): HandleReturnType {
    const {peer_id: peerId, identity: peerIdentity, restrictions} = request;
    if (!inDomain(state, peerId, DomainKeys.Agm)) {
        return join(state, source, peerId, peerIdentity, restrictions);
    } else {
        return [state, []] as HandleReturnType;
    }
}

/**
 * Local peer is joining the domain.
 *
 * Updates the state and generates messages to all existing peers announcing the new peer
 */
function localJoin(state: State, source: Source, request, options?: AgmDomainOptions): HandleReturnType {
    const {peer_id: peerId, request_id: requestId, identity, restrictions} = request;
    if (inDomain(state, peerId, DomainKeys.Agm)) {
        // the peer is already in the domain
        return [state, [success(agmDomainUri, source, requestId, peerId)]];
    } else {
        // we need to join it

        const parsedRestrictions = rst.parse(restrictions) ?? options?.defaultPeerRestrictions;
        let actions;
        [state, actions] = join(state, source, peerId, identity, parsedRestrictions);
        actions = actions.concat([success(agmDomainUri, source, requestId, peerId)]);
        const peer = byIdInDomainThrows(state, peerId, DomainKeys.Agm);
        if (isClusterPeer(DomainKeys.Agm, peer)) {
            const joinMessage = {...request, type: 'join', restrictions: peer[DomainKeys.Agm].restrictions};
            if (peer.options) {
                joinMessage.options = peer.options;
            }
            actions = actions.concat([broadcast(peerToAddress(nodeId(state.ids), peerId), joinMessage)]);
        }
        return [state, actions];
    }
}

export function forwardJoin(state: State, source: Source, request, options?: AgmDomainOptions): HandleReturnType {
    if (isRemoteSource(source)) {
        return remoteJoin(state, source, request);
    } else {
        return localJoin(state, source, request, options);
    }
}

/*Detaches a peer from an existing one*/
function detachPeer(state: State, peer: Peer, fromPeerId: PeerId, reason: Reason): HandleReturnType {
    const fromPeer = byIdThrows(state, fromPeerId);
    const recipient = fromPeer.source;
    let msgs;
    [state, msgs] = threadStateAndActions(
        state,
        (s) => cancelInvocations(s, recipient, fromPeer, peer),
        (s) => cancelInterest(s, fromPeer, peer, undefined, reasonPeerRemoved),
        (s) => removeMethodsFromConsumer(s, peer.id, byIdThrows(s, fromPeer.id)));

    return [state, isLocalSource(recipient) ? msgs.concat(peerRemoved(agmDomainUri, recipient, fromPeerId, peer.id, reason)) : msgs];
}

/*Peer is leaving the domain.

  Updates the state and generates messages for the peer and its registered methods being removed
 */
export function removePeer(state: State, peer: AgmPeer, reason: Reason): HandleReturnType {
    const peerId = peer.id;
    const [newState, messages] = visiblePeers(state, DomainKeys.Agm, peer)
        .map(peer => peer.id)
        .reduce(([s, m], fromId) => {
            const [ns, nm] = detachPeer(s, peer, fromId, reason);
            return [ns, m.concat(nm)];
        }, cancelSubscriptions(state, peer, reason));
    return [leaveDomain(newState, peerId, DomainKeys.Agm), messages];
}

function remoteLeave(state: State, source: Source, request: DomainLeave): HandleReturnType {
    const {peer_id: peerId} = request;
    const peer = byIdInDomain(state, peerId, DomainKeys.Agm);
    if (peer) {
        return removePeer(state, peer, requestToReason(request));
    }
    return [state, []];
}

function localLeave(state: State, source: Source, request: DomainLeave): HandleReturnType {
    const {request_id: requestId, peer_id: peerId} = request;
    const peer = byIdInDomainThrows(state, peerId, DomainKeys.Agm);
    let [newState, actions] = removePeer(state, peer, requestToReason(request))
    actions = actions.concat(success(agmDomainUri, source, requestId, peerId));
    if (isClusterPeer(DomainKeys.Agm, peer)) {
        actions = actions.concat([broadcast(peerToAddress(nodeId(state.ids), peerId), {
            ...request,
            type: 'leave'
        })]);
    }
    return [newState, actions];
}

function leave(state: State, source: Source, request: DomainLeave) {
    if (isRemoteSource(source)) {
        return remoteLeave(state, source, request);
    }
    return localLeave(state, source, request);
}

/* Source has disconnected from the system */
export function sourceRemoved(state: State, source: Source, request): HandleReturnType {
    if (log.enabledFor('debug')) {
        log.debug(`removing source ${JSON.stringify(source)} from agm domain`);
    }
    const affected = bySourceInDomain(state, source, DomainKeys.Agm);
    const stateWithoutAffected = affected
        .map(p => p.id)
        .reduce((s, peerId) => {
            return leaveDomain(s, peerId, DomainKeys.Agm);
        }, state);
    const r: HandleReturnType = affected.reduce(([s, m], peer) => {
        const [ns, nm] = removePeer(s, peer, reasonPeerRemoved);
        return [ns, m.concat(nm)];
    }, [stateWithoutAffected, []] as HandleReturnType);
    if (log.enabledFor('debug')) {
        log.debug(`removed source ${JSON.stringify(source)} from agm domain`);
    }
    return r;
}

/**
 * Handles an error that is coming as a response to either an invocation on subscription.
 *
 *   Returns [<new state> <messages>]
 */
export function handleError(state: State, source: Source, request): HandleReturnType {
    const {request_id: requestId, peer_id: peerId} = request;
    byIdThrows(state, peerId);
    const response = completeInvocation(state, requestId, peerId, request, {failure: request});
    if (response) {
        return response;
    }
    return dropInterest(state, source, request, true);
}

function handleRequest(state: State, source: Source, request: IOGateway.Message<AgmIncomingType | DomainType | CommandType>, options?: AgmDomainOptions): HandleReturnType {

    switch (request.type) {
        case 'domain/join': {
            return forwardJoin(state, source, request as DomainJoin, options)
        }
        case 'domain/leave': {
            return leave(state, source, request as DomainLeave);
        }
        case 'register': {
            return forwardRegister(state, source, request as Register, options);
        }
        case 'unregister': {
            return forwardUnregister(state, source, request as Unregister);
        }
        case 'call': {
            return forwardCall(state, source, request as Call);
        }
        case 'yield': {
            return forwardYield(state, source, request as Yield);
        }
        case 'subscribe': {
            return forwardSubscribe(state, source, request as Subscribe);
        }
        case 'unsubscribe': {
            return forwardUnsubscribe(state, source, request as Unsubscribe);
        }
        case 'drop-subscription': {
            return dropInterest(state, source, request as DropSubscription, false);
        }
        case 'accepted': {
            return forwardAccepted(state, source, request as Accepted);
        }
        case 'publish': {
            return forwardPublish(state, source, request as Publish);
        }
        case 'post': {
            return forwardPost(state, source, request as Post);
        }
        case 'error': {
            return handleError(state, source, request);
        }
        case 'commands/source-removed': {
            return sourceRemoved(state, source, request);
        }
        default: {
            log.error(`Unhandled message ${JSON.stringify(request)}`);
            return [state, [error(agmDomainUri, source, request['request_id'] ?? -1, request['peer_id'], reason(agmUnhandledMessage, `Unhandled message ${JSON.stringify(request)}`))]]
        }
    }
}

class AgmDomain implements Domain<AgmIncomingType> {
    constructor(private readonly options?: AgmDomainOptions) {}
    info(): DomainInfo {
        return {uri: agmDomainUri, description: '', version: 1};
    }

    init(state) {
        return state;
    }

    destroy(state: State) {
        return state;
    }

    handleMessage(state: State, request: NodeMessage<CommandType | DomainType | AgmIncomingType>): HandleReturnType {
        const {source, body} = request;
        try {
            return handleRequest(state, source, body, this.options);
        } catch (e) {
            if (isLocalSource(source)) {
                return [state, [error(agmDomainUri, source, body['request_id'], body['peer_id'], exceptionToReason(e, agmFailure))]]
            }
            return [state, []];
        }
    }

    stateToMessages(state: State): StateToMessagesReturnType {
        const nodeId = state.ids.nodeId;
        const receiver = undefined as unknown as Source;
        const result = peersInDomain(state, DomainKeys.Agm)
            .filter(peer => isLocalSource(peer.source) && isClusterPeer(DomainKeys.Agm, peer))
            .flatMap(peer => {
                const peerId = peer.id;
                const methods = Object.values(peer[DomainKeys.Agm].methods?.[peerId] ?? {})
                    .filter(remoteMethodsFilter(peer, this.options?.defaultMethodRestrictions));
                const messages: IOGateway.Message<'join' | 'register'>[] = [
                    gJoin(undefined, peerId, peer[DomainKeys.Agm].restrictions, agmDomainUri, peer.identity, peer.options),
                    register(undefined, peerId, methods)
                ];
                return messages.map(msg => unicast(peerToAddress(nodeId, peerId), receiver, msg));
            });

        return result;
    }
}
export type AgmDomainOptions = {
    defaultPeerRestrictions?: OriginType;
    defaultMethodRestrictions?: (this: Identity, name?: string) => OriginType | undefined;
}
export function agmDomain(options?: AgmDomainOptions): AgmDomain {
    return new AgmDomain(options);
}
